import {
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconClipboardList, IconClipboardPlus } from '@tabler/icons-react';

import type { PendingServiceRequestCard } from '../utils/pendingServiceRequest';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';

const LOAD_ERROR_MESSAGE =
  "We couldn't load your service requests. Please refresh or contact support if this keeps happening.";

const EMPTY_MESSAGE = "You don't have any pending service requests right now.";

export type PendingServiceRequestsViewState = 'loading' | 'error' | 'empty' | 'populated';

export function resolvePendingServiceRequestsViewState(
  loading: boolean,
  error: string | null,
  requestCount: number,
): PendingServiceRequestsViewState {
  if (loading) return 'loading';
  if (error) return 'error';
  if (requestCount === 0) return 'empty';
  return 'populated';
}

export { EMPTY_MESSAGE as PENDING_SERVICE_REQUESTS_EMPTY_MESSAGE };
export { LOAD_ERROR_MESSAGE as PENDING_SERVICE_REQUESTS_LOAD_ERROR_MESSAGE };

function stageBadgeColor(tone: PendingServiceRequestCard['stageTone']): string {
  switch (tone) {
    case 'warning':
      return 'yellow';
    case 'success':
      return 'green';
    case 'neutral':
      return 'gray';
    default:
      return 'blue';
  }
}

function formatSubmittedDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export interface PendingServiceRequestsSectionProps {
  requests: PendingServiceRequestCard[];
  loading: boolean;
  error: string | null;
  onRequestService?: () => void;
}

function LoadingSkeleton(): JSX.Element {
  return (
    <Stack gap="sm" aria-busy="true" aria-label="Loading pending service requests">
      {[0, 1].map((key) => (
        <Card key={key} withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Skeleton height={16} width="60%" />
            <Skeleton height={12} width="40%" />
            <Skeleton height={12} width="90%" />
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

export function PendingServiceRequestsSection({
  requests,
  loading,
  error,
  onRequestService,
}: PendingServiceRequestsSectionProps): JSX.Element {
  return (
    <Paper p="lg" withBorder>
      <Flex
        justify="space-between"
        align="flex-start"
        gap="sm"
        mb="md"
        wrap="wrap"
        style={{ flexShrink: 0 }}
      >
        <Group gap="xs" wrap="nowrap">
          <IconClipboardList size={18} stroke={1.5} aria-hidden />
          <Title order={3} id="pending-service-requests-heading">
            Pending Service Requests
          </Title>
          <LocalhostOnlyBadge />
        </Group>
        {onRequestService ? (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconClipboardPlus size={14} />}
            onClick={onRequestService}
          >
            Request a service
          </Button>
        ) : null}
      </Flex>

      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <Alert variant="light" color="red" title="Unable to load requests">
          <Text size="sm">{LOAD_ERROR_MESSAGE}</Text>
        </Alert>
      ) : requests.length === 0 ? (
        <Text size="sm" c="gray.6">
          {EMPTY_MESSAGE}
        </Text>
      ) : (
        <Stack gap="sm" aria-labelledby="pending-service-requests-heading">
          {requests.map((item) => (
            <Card key={item.id} withBorder padding="md" radius="md">
              <Stack gap="xs">
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  justify="space-between"
                  align={{ base: 'flex-start', sm: 'flex-start' }}
                  gap="xs"
                >
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} lineClamp={2}>
                      {item.serviceTypeLabel}
                    </Text>
                    {item.submittedAt ? (
                      <Text size="xs" c="gray.6">
                        Submitted {formatSubmittedDate(item.submittedAt)}
                      </Text>
                    ) : null}
                  </Stack>
                  <Badge
                    variant="light"
                    color={stageBadgeColor(item.stageTone)}
                    size="sm"
                    style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                  >
                    {item.stageLabel}
                  </Badge>
                </Flex>
                <Text size="sm" c="gray.7">
                  {item.nextStepText}
                </Text>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {loading && (
        <Group justify="center" py="xs" mt="xs" aria-hidden>
          <Loader size="xs" />
        </Group>
      )}
    </Paper>
  );
}
